import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { LIST_RECENT_CALL_SESSIONS } from '../../graphql/operations';

const POLL_INTERVAL_MS = 5000;

// "Active" = in any state where an operator might still need to look at,
// intervene on, or wait for a downstream provider action. Includes the new
// outbound_* statuses so a queued outbound call gets surfaced (otherwise
// the LiveVoicePanel "Active calls" KPI says 0 while the operator's just-
// queued call sits invisible). 'ended' / 'rejected_pilot' / 'outbound_failed'
// stay out — those are terminal states that belong in an audit/log surface,
// not the live operator queue.
const ACTIVE_STATUSES = new Set([
  'inbound_received',
  'accepted',
  'authenticating',
  'authenticated',
  'transferring',
  'outbound_queued',
  'outbound_dialing',
  'outbound_ringing',
  'outbound_in_progress',
]);

function isActive(session) {
  if (!session) return false;
  if (session.endedAt) return false;
  return ACTIVE_STATUSES.has(String(session.status || ''));
}

export default function useLiveCalls({ limit = 25 } = {}) {
  // activeOnly=true makes the backend scan its full GSI1 page (200) and apply
  // the active-status filter SERVER-SIDE before returning. Without this flag
  // the backend returned the most recent N rows and we filtered client-side,
  // so an old still-active call could be silently dropped if N newer terminal
  // calls existed (round-6 finding). The backend now bounds the returned set
  // by the active count, not by total recency.
  const { data, loading, error, refetch } = useQuery(LIST_RECENT_CALL_SESSIONS, {
    variables: { limit, activeOnly: true },
    pollInterval: POLL_INTERVAL_MS,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false,
  });

  const sessions = useMemo(() => data?.listRecentCallSessions || [], [data]);

  const active = useMemo(() => sessions.filter(isActive), [sessions]);
  const inAuth = useMemo(
    () => active.filter((s) => s.authState && s.authState !== 'authenticated'),
    [active]
  );
  const transferring = useMemo(
    () => active.filter((s) => s.transferState === 'requested' || s.transferState === 'completed'),
    [active]
  );

  return {
    sessions,
    active,
    inAuth,
    transferring,
    loading,
    error,
    refetch,
  };
}
